import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "app/api";
import { clearAppData, removeCookie, storeData } from "utils/storage";

export interface AuthInterface {
  LOGIN: {
    nama?: string;
    unik_id?: string;
    emailAddr: string;
    password: string;
  }
}

export interface RefreshTokenInterface {
  token: string;
}

export interface ForgotPasswordInterface {
  FORGOT_PASSWORD: {
    Email: string
  }
}

export interface VerifiedForgotPasswordInterface {
  FORGOT_PASSWORD: {
    Token: string
  }
}

export interface ChangePasswordInterface {
  CHANGE_PASSWORD: {
    Userid: string
    Password: string
  }
}

export interface RegisterInterface {
  REGISTER: {
    emailAddr: string;
    nama: string;
    role: string;
    password: string;
  }
}


export const signUp = createAsyncThunk(
  "signUp",
  async (data: RegisterInterface, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/sign-up",
      data,
    };

    return api(config).then(
      (res: any) => {
        return res.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.message);
      }
    );
  }
);

export const signIn = createAsyncThunk(
  "signIn",
  async (data: any, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/sign-in-pendamping",
      data: data.data,
      withCredentials: true,
      headers: data.header,
    };

    return api(config).then(
      (res: any) => {
        storeData("token", res.data.LOGIN.token)
        return res.data.LOGIN.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.LOGIN.data);
      }
    );
  }
);

export const signInUser = createAsyncThunk(
  "signInUser",
  async (data: any, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/sign-in",
      data: data.data,
      withCredentials: true,
      headers: data.header,
    };

    return api(config).then(
      (res: any) => {
        storeData("token", res.data.LOGIN.token)
        return res.data.LOGIN.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.LOGIN.data);
      }
    );
  }
);

export const signOut = createAsyncThunk(
  "signOut",
  async (data: any, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/sign-out",
      withCredentials: true,
    };

    return api(config).then(
      (res: any) => {

        clearAppData()

        return res.data;
      },
      (err: any) => {

        if (err.response.status === 401) {
          clearAppData()
          window.location.reload();
        }

        return thunkAPI.rejectWithValue(err.response.data.LOGOUT.message);
      }
    );
  }
);

export const refreshToken = createAsyncThunk(
  "refreshToken",
  async (data: RefreshTokenInterface, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/refresh-token",
      data,
    };

    return api(config).then(
      (res: any) => {
        return res.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.message);
      }
    );
  }
);

export const forgotPassword = createAsyncThunk(
  "forgotPassword",
  async (data: { body: ForgotPasswordInterface, headers: any }, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/forgot-password-by-email",
      data: data.body,
      headers: data.headers,
    };

    return api(config).then(
      (res: any) => {
        return res.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.message);
      }
    );
  }
);

export const verifiedForgotPassword = createAsyncThunk(
  "verifiedForgotPassword",
  async (data: { body: VerifiedForgotPasswordInterface, headers: any }, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/verified-forgot-password-by-email",
      data: data.body,
      headers: data.headers,
    };

    return api(config).then(
      (res: any) => {
        return res.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.message);
      }
    );
  }
);

export const changePassword = createAsyncThunk(
  "changePassword",
  async (data: { body: ChangePasswordInterface, headers: any }, thunkAPI) => {
    const config = {
      method: "post",
      url: "auth/change-password",
      data: data.body,
      headers: data.headers,
    };

    return api(config).then(
      (res: any) => {
        return res.data;
      },
      (err: any) => {
        return thunkAPI.rejectWithValue(err.response.data.message);
      }
    );
  }
);

const AuthSlice = createSlice({
  name: "AuthSlice",
  initialState: {
    data: { LOGIN: { nama: "", emailAddr: "", password: "", unik_id: "" } } as AuthInterface,
    isFetching: false,
    isSuccess: false,
    isSignInSuccess: false,
    isSignOutSuccess: false,
    isForgotPasswordSuccess: false,
    isVerifiedForgotPasswordSuccess: false,
    isChangePasswordSuccess: false,
    isError: false,
    isAuth: false,
    errorMessage: "" as string,
  },
  reducers: {
    clearState: (state, action) => {
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = false;
      state.isSignInSuccess = false;
      state.isSignOutSuccess = false;
      state.isForgotPasswordSuccess = false;
      state.isVerifiedForgotPasswordSuccess = false;
      state.errorMessage = "";
    },
    setAuth: (state, action) => {
      state.isAuth = action.payload;
    },
  },
  extraReducers: (builder) => {
    // signInUser
    builder.addCase(signInUser.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isSignInSuccess = true;
      state.isAuth = true;
      state.data = payload;
      return state;
    });
    builder.addCase(signInUser.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.isAuth = false;
      state.errorMessage = payload.payload;
    });
    builder.addCase(signInUser.pending, (state, action) => {
      state.isFetching = true;
      state.isAuth = false;
    });

    // signIn
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isSignInSuccess = true;
      state.isAuth = true;
      state.data = payload;
      return state;
    });
    builder.addCase(signIn.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.isAuth = false;
      state.errorMessage = payload.payload;
    });
    builder.addCase(signIn.pending, (state, action) => {
      state.isFetching = true;
      state.isAuth = false;
    });

    // signUp
    builder.addCase(signUp.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      return state;
    });
    builder.addCase(signUp.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.payload;
    });
    builder.addCase(signUp.pending, (state, action) => {
      state.isFetching = true;
    });

    // refreshToken
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isAuth = true;
      return state;
    });
    builder.addCase(refreshToken.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.isAuth = false;
      state.errorMessage = payload.payload;
    });
    builder.addCase(refreshToken.pending, (state, action) => {
      state.isFetching = true;
    });

    // signout
    builder.addCase(signOut.fulfilled, (state, { payload }) => {
      removeCookie("token");
      state.isFetching = false;
      state.isSignOutSuccess = true;
      state.isAuth = false;
      return state;
    });
    builder.addCase(signOut.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.payload;
    });
    builder.addCase(signOut.pending, (state, action) => {
      state.isFetching = true;
    });

    // ForgotPassword
    builder.addCase(forgotPassword.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isForgotPasswordSuccess = true;
      return state;
    });
    builder.addCase(forgotPassword.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.payload;
    });
    builder.addCase(forgotPassword.pending, (state, action) => {
      state.isFetching = true;
    });


    // VerifiedForgotPassword
    builder.addCase(verifiedForgotPassword.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isVerifiedForgotPasswordSuccess = true;
      return state;
    });
    builder.addCase(verifiedForgotPassword.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.payload;
    });
    builder.addCase(verifiedForgotPassword.pending, (state, action) => {
      state.isFetching = true;
    });

    // VerifiedForgotPassword
    builder.addCase(changePassword.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isChangePasswordSuccess = true;
      return state;
    });
    builder.addCase(changePassword.rejected, (state, payload: any) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.payload;
    });
    builder.addCase(changePassword.pending, (state, action) => {
      state.isFetching = true;
    });
  },
});

export const { clearState, setAuth } = AuthSlice.actions;
export default AuthSlice.reducer;
